import {
  FeatureToggleIdentifier,
  isFeatureEnabled,
} from "@/services/feature-toggle-adapter";

/**
 * Checks if debug console logging is enabled via feature toggle
 * @returns True if ENABLE_DEBUG_CONSOLE_LOGS feature toggle is enabled
 */
function isDebugLoggingEnabled(): boolean {
  return isFeatureEnabled(FeatureToggleIdentifier.ENABLE_DEBUG_CONSOLE_LOGS);
}

/**
 * Log categories with emoji prefixes for visual scanning
 */
const LogCategory = {
  STATE_CHANGE: "🔄",
  GUARD: "🔍",
  STATE_ENTRY: "📍",
  DECISION_STATE: "🔀",
  EVENT: "👤",
  DOCTOR_EVENT: "🏥",
  NURSE_EVENT: "🩺",
} as const;

type LogContext = Record<string, any>;

/**
 * Core debug logging function - only logs when feature toggle is enabled
 * Provides early return for zero overhead when disabled
 *
 * @param category - Log category emoji prefix
 * @param label - Descriptive label for the log entry
 * @param context - Context object with relevant data
 */
function debugLog(category: string, label: string, context?: LogContext): void {
  if (!isDebugLoggingEnabled()) {
    return;
  }

  if (context) {
    console.log(`${category} ${label}:`, context);
  } else {
    console.log(`${category} ${label}`);
  }
}

export const debugLogger = {
  stateChange: (label: string, context: LogContext) => {
    debugLog(
      LogCategory.STATE_CHANGE,
      `[IntercareMedicalAssistance] ${label}`,
      context
    );
  },

  guard: (guardName: string, context: LogContext) => {
    debugLog(LogCategory.GUARD, `[Guard] ${guardName}`, context);
  },

  stateEntry: (stateName: string, context: LogContext) => {
    debugLog(LogCategory.STATE_ENTRY, `[State Entry] ${stateName}`, context);
  },

  decisionState: (stateName: string, context: LogContext) => {
    debugLog(
      LogCategory.DECISION_STATE,
      `[Decision State] ${stateName}`,
      context
    );
  },

  event: (eventName: string, context: LogContext) => {
    debugLog(
      LogCategory.EVENT,
      `[IntercareMedicalAssistance] ${eventName}`,
      context
    );
  },

  doctorEvent: (eventName: string, context: LogContext) => {
    debugLog(
      LogCategory.DOCTOR_EVENT,
      `[IntercareMedicalAssistance] ${eventName}`,
      context
    );
  },

  nurseEvent: (eventName: string, context: LogContext) => {
    debugLog(
      LogCategory.NURSE_EVENT,
      `[IntercareMedicalAssistance] ${eventName}`,
      context
    );
  },
};
